import { canvasPreview, canvasToBlob } from "@/utils/helpers/canvas-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import { Checkbox, Row } from "antd";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import ReactCrop, {
  Crop,
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
  type PixelCrop,
} from "react-image-crop";
import BasicModal, {
  BasicModalProps,
} from "@components/core/modals/BasicModal";

// ! Very important import, do not remove it must be imported in order to <ReactCrop /> to work properly (https://github.com/sekoyo/react-image-crop)
import { CanvasFileResult } from "@/type/core/image/image.types";
import "react-image-crop/dist/ReactCrop.css";
import { computeBase64ImageSizeInBytes } from "@/utils/helpers/image-helper";

const DEFAULT_CROP: PixelCrop = {
  width: 150,
  height: 50,
  x: 25,
  y: 25,
  unit: "px",
};
const ASPECT = 150 / 36;
const SIZE_LIMIT = 200000;

interface CropModalProps extends BasicModalProps {
  sourcePicture: string;
  sourceImageType: string;
  onUpdatePicture: (file: File) => void;
}

const CropModal: FunctionComponent<CropModalProps> = ({
  sourcePicture,
  sourceImageType,
  onUpdatePicture,
  visible,
  onCancel,
  ...props
}) => {
  const { t } = useTranslation();

  const [imgKey, setImgKey] = useState("");
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>(DEFAULT_CROP);
  const [croppedImage, setCroppedImage] = useState<File>();
  const [keepRatio, setKeepRatio] = useState<boolean>(true);
  const [aspect, setAspect] = useState<number | undefined>(ASPECT);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const imageTooBig = croppedImage
    ? croppedImage.size > SIZE_LIMIT
    : computeBase64ImageSizeInBytes(sourcePicture) > SIZE_LIMIT;

  useEffect(() => {
    // As this is presented in a Modal the component is not remount every time modal is visible so that it does not do the lifecycle correctly.
    // To prevent many UI/UX glitch we force a useEffect that work like a mount/unmount based on the visible variable
    if (visible) {
      // mount
      // Changing the key of the <img /> below trigger the re render and moreover the "onLoad" which is our main utility because we render a correct crop due to this
      setImgKey(`CropModal_imgKey_${Date.now()}`);
    } else {
      // unmount
    }
  }, [visible]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imageRef.current &&
        canvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(imageRef.current, canvasRef.current, completedCrop);
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [completedCrop]);

  const handleCropComplete = (completedCrop: PixelCrop) => {
    setCompletedCrop(completedCrop);
    makeClientCrop(completedCrop, crop);
  };

  const makeClientCrop = (completedCrop: PixelCrop, crop?: Crop) => {
    if (imageRef?.current && crop?.width && crop?.height && completedCrop) {
      getCroppedImg(imageRef.current, completedCrop, "newFile.png")
        .then((res) => {
          setCroppedImage(res.file);
        })
        .catch(() => {
          toastError(t("error.cropImage"));
          setCroppedImage(undefined);
        });
    }
  };

  const getCroppedImg = useCallback(
    async (
      image: HTMLImageElement,
      crop: PixelCrop,
      fileName: string,
    ): Promise<CanvasFileResult> => {
      return new Promise((resolve, reject) => {
        const canvas = canvasRef?.current;
        if (canvas) {
          canvasPreview(image, canvas, crop);
          canvasToBlob(canvas, fileName, sourceImageType)
            .then((canvasFile) => {
              resolve(canvasFile);
            })
            .catch((error: string) => {
              reject(new Error(error));
            });
        } else {
          reject(new Error("canvasRef.current is undefined"));
        }
      });
    },
    [sourceImageType],
  );

  const handleButtonClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();

    if (!croppedImage) {
      return;
    }
    if (!imageTooBig) {
      onUpdatePicture(croppedImage);

      // Close the modal
      onCancel();
    }
  };

  const handleCancelClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();

    setCroppedImage(undefined);
    setCompletedCrop(DEFAULT_CROP);
    setCrop(undefined);

    // Close the modal
    onCancel();
  };

  const handleCheckboxChange = () => {
    if (aspect) {
      setAspect(undefined);
      setKeepRatio(false);
    } else {
      setKeepRatio(true);
      setAspect(ASPECT);

      if (imageRef.current) {
        const { width, height } = imageRef.current;
        const newCrop = centerAspectCrop(width, height, ASPECT);
        setCrop(newCrop);
        // Updates the preview
        setCompletedCrop(convertToPixelCrop(newCrop, width, height));
      }
    }
  };

  const centerAspectCrop = (
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
  ) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight,
      ),
      mediaWidth,
      mediaHeight,
    );
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const percentCrop = centerAspectCrop(width, height, aspect ?? ASPECT);
    setCrop(percentCrop);
    handleCropComplete(convertToPixelCrop(percentCrop, width, height));
  };

  return (
    <BasicModal
      {...props}
      title={t("offices.preferences.editImage")}
      visible={visible}
      onCancel={handleCancelClick}
      onOk={handleButtonClick}
      okButtonDisabled={imageTooBig}
      buttonText={t("modal.save")}
      isClosableWhenClickingOutside={false}
    >
      <div className="container-padding-m">
        <Checkbox
          id={"CropModalCheckboxId"}
          checked={keepRatio}
          onChange={handleCheckboxChange}
          className="spacer-bottom"
          disabled={imageTooBig}
        >
          {t("account.mySignature.signaturePicture.cropModal.keepRatio")}
        </Checkbox>
        {imageTooBig && (
          <Row className={"mb-3 justify-content-center text-danger"}>
            {t("account.mySignature.signaturePicture.cropModal.imageTooBig")}
          </Row>
        )}
        {!imageTooBig && !!sourcePicture && (
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => handleCropComplete(c)}
            aspect={aspect}
            className="spacer-bottom"
          >
            <img
              key={imgKey}
              ref={imageRef}
              alt="Crop me"
              src={sourcePicture}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        )}
        {!imageTooBig && !!completedCrop && (
          <canvas className="large canvas spacer-bottom" ref={canvasRef} />
        )}
      </div>
    </BasicModal>
  );
};

export default CropModal;
